import * as React from "react"

import { Role } from "@/lib/roles"
import { connectReview, type ConnectionStatus, type InboundFrame } from "@/lib/ws"

import type { LiveState, ReviewId, UserId } from "../data/review-data"

/**
 * Realtime channel for a single review. Transport is `lib/ws.ts`; this
 * module is the public consumer surface for the workspace.
 *
 * Contract:
 *   • `status` is the authoritative three-state liveness
 *     (`live` / `reconnecting` / `offline`) that the header indicator
 *     renders. A socket being open is not evidence of end-to-end
 *     delivery — but is a reasonable proxy for it in this app.
 *   • `presence` is a role-only projection of who else is looking at
 *     the review. Names are never broadcast to client channels, because
 *     staff identity is confidential in the counterparty view.
 *   • `cursors` carry PDF-point coordinates so remote cursors track the
 *     document at any zoom without recomputation. Entries are keyed by
 *     `userId`; a late-arriving frame from a user who has already
 *     departed cannot resurrect them.
 *   • Invalidation frames dispatched through `useLiveInvalidation` are
 *     coalesced inside a short window so a burst of updates collapses
 *     to a single refetch.
 */

export interface PresenceViewer {
  userId: UserId
  role: string
}

export interface RemoteCursor {
  userId: UserId
  role: string
  page: number
  x: number
  y: number
  updatedAt: number
}

export interface UseReviewLiveResult {
  status: LiveState
  lastFrameAt: number | null
  presence: PresenceViewer[]
  cursors: RemoteCursor[]
  /** Who is composing. Carries a name only where the reader is staff. */
  typing: TypingViewer[]
}

/**
 * The frame types this module acts on.
 *
 * Declared once and used by the handlers below, so a test can assert the set
 * against what `ws/hub.py` publishes. Presence shipped broken precisely
 * because the two drifted silently: the server sent `presence.joined` and the
 * client matched `presence`, with nothing to notice.
 */
export const HANDLED_FRAME_TYPES = [
  "comment.created",
  "review.updated",
  "presence",
  "presence.joined",
  "presence.left",
  "typing",
  "cursor.moved",
] as const satisfies readonly InboundFrame["type"][]

/** Someone composing: their role, and their name where the reader may see it. */
export interface TypingViewer {
  role: string
  name?: string
}

/** Internal: the same, plus when the last frame arrived, for expiry. */
interface TypingSeen {
  at: number
  name?: string
}

const CURSOR_TTL_MS = 8_000

/**
 * How long a `typing` frame keeps someone in the list.
 *
 * The server throttles a composing client to one frame per
 * `ws_typing_min_interval_seconds` and sends nothing when they stop, so the
 * window has to outlast that interval without outlasting human patience.
 * Four seconds is the usual chat figure and comfortably clears the throttle.
 */
const TYPING_TTL_MS = 4_000

function toLiveState(status: ConnectionStatus): LiveState {
  if (status === "open") return "live"
  if (status === "connecting" || status === "reconnecting") return "reconnecting"
  return "offline"
}

export function useReviewLive(reviewId: ReviewId | null): UseReviewLiveResult {
  const [status, setStatus] = React.useState<LiveState>("offline")
  const [lastFrameAt, setLastFrameAt] = React.useState<number | null>(null)
  const [presence, setPresence] = React.useState<PresenceViewer[]>([])
  const [cursors, setCursors] = React.useState<RemoteCursor[]>([])
  // Keyed by role label; the value holds when it was last seen composing and,
  // for a staff reader, who was composing.
  const [typing, setTyping] = React.useState<Record<string, TypingSeen>>({})

  React.useEffect(() => {
    if (!reviewId) {
      setStatus("offline")
      setPresence([])
      setCursors([])
      setTyping({})
      return
    }

    const connection = connectReview(reviewId)
    const offStatus = connection.onStatus((s) => setStatus(toLiveState(s)))
    const offFrame = connection.onFrame((frame: InboundFrame) => {
      setLastFrameAt(Date.now())
      // Written out rather than tested against a list: an `includes` check
      // does not narrow a discriminated union, and `frame.viewers` below needs
      // the narrowing. HANDLED_FRAME_TYPES records the same set for the test.
      if (
        frame.type === "presence" ||
        frame.type === "presence.joined" ||
        frame.type === "presence.left"
      ) {
        // The backend sends `viewers` as a role-only string list (routes_ws.py
        // `_viewer_label`). Identities are withheld on purpose — a client-role
        // reader must not receive staff user_ids. We synthesize a stable
        // per-role key so React `key=` stays stable while multiple people of
        // the same role are present, using the index as a tiebreaker.
        setPresence(
          frame.viewers
            .filter((role): role is string => typeof role === "string" && role.length > 0)
            .map((role, i) => ({
              userId: `${role}-${String(i)}` as UserId,
              role,
            })),
        )
        return
      }
      if (frame.type === "typing") {
        // There is no "stopped typing" frame (hub.broadcast_typing is
        // fire-and-forget), so an entry is held for a short window and allowed
        // to lapse. A stale indicator is worse than a late one: it claims
        // someone is still writing when they have gone.
        const viewer = frame.viewer
        if (typeof viewer !== "string" || viewer.length === 0) return
        // Keyed by role so two people of one role collapse to a single line —
        // the server sends no per-person id, by design. The name rides along
        // when the reader is staff; a client's socket never receives one.
        setTyping((prev) => ({
          ...prev,
          [viewer]: { at: Date.now(), ...(frame.viewer_name ? { name: frame.viewer_name } : {}) },
        }))
        return
      }
      if (frame.type === "cursor.moved") {
        const c = frame.cursor
        // Malformed frames get dropped rather than pushed as junk cursors.
        if (typeof c?.user_id !== "string" || c.user_id.length === 0) return
        setCursors((prev) => {
          const now = Date.now()
          const kept = prev.filter(
            (existing) => existing.userId !== c.user_id && now - existing.updatedAt < CURSOR_TTL_MS,
          )
          kept.push({
            userId: c.user_id as UserId,
            role: typeof c.role === "string" && c.role.length > 0 ? c.role : Role.VIEWER,
            page: typeof c.page === "number" ? c.page : 1,
            x: typeof c.x === "number" ? c.x : 0,
            y: typeof c.y === "number" ? c.y : 0,
            updatedAt: typeof c.updated_at === "number" ? c.updated_at : Date.now(),
          })
          return kept
        })
      }
    })

    return () => {
      offFrame()
      offStatus()
      connection.close()
    }
  }, [reviewId])

  // Expiry is driven by a tick rather than a timer per frame: one interval for
  // the whole hook, and it stops as soon as nobody is typing.
  const [now, setNow] = React.useState(() => Date.now())
  const someoneTyping = Object.keys(typing).length > 0
  React.useEffect(() => {
    if (!someoneTyping) return
    const id = setInterval(() => {
      setNow(Date.now())
    }, 1_000)
    return () => {
      clearInterval(id)
    }
  }, [someoneTyping])

  const activeTyping = React.useMemo(
    () =>
      Object.entries(typing)
        .filter(([, seen]) => now - seen.at < TYPING_TTL_MS)
        .map(([role, seen]) => ({ role, ...(seen.name ? { name: seen.name } : {}) })),
    [typing, now],
  )

  return { status, lastFrameAt, presence, cursors, typing: activeTyping }
}

/**
 * Tell the review's other viewers that this one is composing.
 *
 * Throttled a little above the server's own `ws_typing_min_interval_seconds`
 * (1.0s today): the server silently drops a frame that arrives inside its
 * window, so sending faster spends socket traffic to be ignored. Erring
 * slightly slow costs nothing — the receiver holds each frame for several
 * seconds anyway.
 *
 * Returns a stable callback safe to attach to every keystroke.
 */
export function useTypingSignal(reviewId: ReviewId | null): () => void {
  const lastSentAt = React.useRef(0)

  return React.useCallback(() => {
    if (!reviewId) return
    const now = Date.now()
    if (now - lastSentAt.current < TYPING_SEND_INTERVAL_MS) return
    lastSentAt.current = now
    // Fire-and-forget by design (hub.broadcast_typing): a lost typing frame is
    // not worth a retry, and the socket may legitimately be down.
    connectReview(reviewId).send({ type: "typing" })
  }, [reviewId])
}

const TYPING_SEND_INTERVAL_MS = 1_500
